// BillingConfig.jsx
import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { billingService } from "@/api/billing";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleInput } from "@components/TaleInput";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { InternalServerError } from "@components/Errors/InternalServerError";

const BillingConfig = () => {
    const queryClient = useQueryClient();

    // States
    const [responsePrice, setResponsePrice] = useState(0);
    const [newQuestionTier, setNewQuestionTier] = useState({ quantity: 1, price: 0 });
    const [editingQuestionTier, setEditingQuestionTier] = useState(null);
    const [editingCriteria, setEditingCriteria] = useState({});
    const [newCriteria, setNewCriteria] = useState({ label: "", price: 0 });

    // Fetch all billing data
    const {
        data: billingData,
        isLoading,
        error
    } = useQuery({
        queryKey: ["billing-config"],
        queryFn: async () => {
            const [responsePricing, questionTiers, criteria] = await Promise.all([
                 billingService.getSurveyResponsePricing(),
                billingService.getAllSurveyQuestionTiers(),
                billingService.getAllSurveyCriteria()
            ]);
            return { responsePricing, questionTiers, criteria };
        },
        retry: false,
    });

    // Update response pricing mutation
    const updateResponsePricingMutation = useMutation({
        mutationFn: (price) => billingService.updateResponsePricing(price),
        onSuccess: () => {
            queryClient.invalidateQueries(["billing-config"]);
            alert("Prix par réponse mis à jour avec succès !");
        },
        onError: (error) => {
            console.error("Update error:", error);
            alert("Erreur lors de la mise à jour du prix");
        },
    });

    // Add question tier mutation
    const addQuestionTierMutation = useMutation({
        mutationFn: (data) => billingService.addQuestionTier(data),
        onSuccess: () => {
            queryClient.invalidateQueries(["billing-config"]);
            setNewQuestionTier({ quantity: 1, price: 0 });
            alert("Tranche de questions ajoutée avec succès !");
        },
        onError: (error) => {
            console.error("Add error:", error);
            alert("Erreur lors de l'ajout de la tranche");
        },
    });

    // Update question tier mutation
    const updateQuestionTierMutation = useMutation({
        mutationFn: ({ id, data }) => billingService.updateQuestionTier(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(["billing-config"]);
            setEditingQuestionTier(null);
            alert("Tranche de questions mise à jour avec succès !");
        },
        onError: (error) => {
            console.error("Update error:", error);
            alert("Erreur lors de la mise à jour de la tranche");
        },
    });

    // Update criteria mutation
    const updateCriteriaMutation = useMutation({
        mutationFn: ({ id, data }) => billingService.updateSurveyCriteria(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(["billing-config"]);
            setEditingCriteria({});
            alert("Critère mis à jour avec succès !");
        },
        onError: (error) => {
            console.error("Update error:", error);
            alert("Erreur lors de la mise à jour du critère");
        },
    });

    // Initialize form with fetched data
    useEffect(() => {
        if (billingData?.responsePricing) {
            setResponsePrice(billingData.responsePricing.pricePerResponse || 0);
        }
    }, [billingData]);

    const handleUpdateResponsePrice = () => {
        if (!responsePrice || responsePrice <= 0) {
            alert("Veuillez entrer un prix valide");
            return;
        }
        updateResponsePricingMutation.mutate(parseFloat(responsePrice));
    };

    const handleAddQuestionTier = () => {
        if (!newQuestionTier.quantity || !newQuestionTier.price) {
            alert("Veuillez remplir tous les champs");
            return;
        }
        addQuestionTierMutation.mutate({
            quantity: parseInt(newQuestionTier.quantity),
            price: parseFloat(newQuestionTier.price)
        });
    };

    const handleUpdateQuestionTier = (id, data) => {
        updateQuestionTierMutation.mutate({ id, data });
    };

    const handleUpdateCriteria = (id, data) => {
        updateCriteriaMutation.mutate({ id, data });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-TN', {
            style: 'currency',
            currency: 'TND'
        }).format(amount || 0);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <TaleLoadingFallback />
            </div>
        );
    }

    if (error) {
        return <InternalServerError />;
    }

    const { responsePricing, questionTiers = [], criteria = [] } = billingData || {};

    return (
            <TaleDashbaordMainCard className="p-6">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Configuration de Facturation</h1>
                    <p className="text-gray-600 mt-2">
                        Configurez les prix et les critères de facturation pour les sondages
                    </p>
                    <hr/>
                </div>
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-sm text-blue-600 font-medium">Prix par réponse</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {formatCurrency(responsePricing?.pricePerResponse || 0)}
                                </div>
                            </div>
                            <i className="fas fa-receipt text-3xl text-blue-400"></i>
                        </div>
                        <div className="mt-3 text-sm text-blue-700">
                            Prix appliqué par réponse de sondage
                        </div>
                    </div>

                    <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-sm text-green-600 font-medium">Tranches de questions</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {questionTiers.length}
                                </div>
                            </div>
                            <i className="fas fa-layer-group text-3xl text-green-400"></i>
                        </div>
                        <div className="mt-3 text-sm text-green-700">
                            Niveaux de tarification par nombre de questions
                        </div>
                    </div>

                    <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-sm text-purple-600 font-medium">Critères de facturation</div>
                                <div className="text-2xl font-bold text-gray-900">
                                    {criteria.length}
                                </div>
                            </div>
                            <i className="fas fa-filter text-3xl text-purple-400"></i>
                        </div>
                        <div className="mt-3 text-sm text-purple-700">
                            Critères supplémentaires avec prix
                        </div>
                    </div>
                </div>

                {/* Main Configuration Sections */}
                <div className="space-y-8">
                    {/* Response Pricing Section */}
                    <div className="border-t pt-8">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4">
                                <i className="fas fa-receipt text-blue-500 text-xl"></i>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Prix par réponse de sondage</h2>
                                <p className="text-gray-600 text-sm">
                                    Prix appliqué pour chaque réponse reçue
                                </p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <div className="flex flex-col md:flex-row md:items-end gap-4">
                                <div className="flex-1">
                                    <TaleInput
                                        label="Prix par réponse (DT)"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={responsePrice}
                                        onChange={(e) => setResponsePrice(e.target.value)}
                                        placeholder="Ex: 0.50"
                                        helperText="Ce prix sera multiplié par le nombre de réponses reçues"
                                    />
                                </div>
                                <div className="md:pb-2">
                                    <TaleButton
                                        onClick={handleUpdateResponsePrice}
                                        loading={updateResponsePricingMutation.isPending}
                                    >
                                        Mettre à jour
                                    </TaleButton>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Question Tiers Section */}
                    <div className="border-t pt-8">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center">
                                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center mr-4">
                                    <i className="fas fa-layer-group text-green-500 text-xl"></i>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Tranches de questions</h2>
                                    <p className="text-gray-600 text-sm">
                                        {questionTiers.length} tranche(s) configurée(s)
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Add New Tier Form */}
                        <div className="mb-8 p-6 bg-gray-50 rounded-xl border">
                            <h3 className="font-semibold text-gray-900 mb-4">
                                <i className="fas fa-plus-circle text-green-500 mr-2"></i>
                                Ajouter une nouvelle tranche
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <TaleInput
                                    label="Nombre de questions"
                                    type="number"
                                    min="1"
                                    value={newQuestionTier.quantity}
                                    onChange={(e) => setNewQuestionTier({
                                        ...newQuestionTier,
                                        quantity: e.target.value
                                    })}
                                    placeholder="Ex: 10"
                                />
                                <TaleInput
                                    label="Prix (DT)"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={newQuestionTier.price}
                                    onChange={(e) => setNewQuestionTier({
                                        ...newQuestionTier,
                                        price: e.target.value
                                    })}
                                    placeholder="Ex: 25.00"
                                />
                                <div className="flex items-end">
                                    <TaleButton
                                        onClick={handleAddQuestionTier}
                                        loading={addQuestionTierMutation.isPending}
                                        className="w-full bg-green-600 hover:bg-green-700 border-green-600"
                                    >
                                        <i className="fas fa-plus mr-2"></i>
                                        Ajouter
                                    </TaleButton>
                                </div>
                            </div>
                        </div>

                        {/* Existing Tiers Table */}
                        {questionTiers.length > 0 ? (
                            <div className="overflow-x-auto border rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Nombre de questions
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Prix
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {questionTiers.map((tier) => (
                                        <tr key={tier.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {editingQuestionTier?.id === tier.id ? (
                                                    <div className="w-32">
                                                        <TaleInput
                                                            type="number"
                                                            min="1"
                                                            value={editingQuestionTier.quantity}
                                                            onChange={(e) => setEditingQuestionTier({
                                                                ...editingQuestionTier,
                                                                quantity: e.target.value
                                                            })}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="font-medium text-gray-900">{tier.quantity}</div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {editingQuestionTier?.id === tier.id ? (
                                                    <div className="w-32">
                                                        <TaleInput
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={editingQuestionTier.price}
                                                            onChange={(e) => setEditingQuestionTier({
                                                                ...editingQuestionTier,
                                                                price: e.target.value
                                                            })}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="font-medium text-green-600">
                                                        {formatCurrency(tier.price)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex gap-2">
                                                    {editingQuestionTier?.id === tier.id ? (
                                                        <>
                                                            <TaleButton
                                                                size="sm"
                                                                onClick={() => handleUpdateQuestionTier(tier.id, {
                                                                    quantity: parseInt(editingQuestionTier.quantity),
                                                                    price: parseFloat(editingQuestionTier.price)
                                                                })}
                                                                loading={updateQuestionTierMutation.isPending}
                                                            >
                                                                <i className="fas fa-check mr-1"></i>
                                                                Valider
                                                            </TaleButton>
                                                            <TaleButton
                                                                size="sm"
                                                                variant="outlined"
                                                                onClick={() => setEditingQuestionTier(null)}
                                                            >
                                                                Annuler
                                                            </TaleButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <TaleButton
                                                                size="sm"
                                                                variant="outlined"
                                                                onClick={() => setEditingQuestionTier({
                                                                    id: tier.id,
                                                                    quantity: tier.quantity,
                                                                    price: tier.price
                                                                })}
                                                            >
                                                                <i className="fas fa-edit mr-1"></i>
                                                                Modifier
                                                            </TaleButton>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <i className="fas fa-layer-group text-4xl text-gray-300 mb-3"></i>
                                <p className="text-gray-500">Aucune tranche de questions configurée</p>
                                <p className="text-sm text-gray-400 mt-1">Ajoutez votre première tranche ci-dessus</p>
                            </div>
                        )}

                        <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex items-start">
                                <i className="fas fa-lightbulb text-green-500 mt-0.5 mr-3"></i>
                                <div className="text-sm text-green-700">
                                    Les tranches définissent des prix par plage de questions.
                                    Exemple: 1-10 questions = 25DT, 11-20 questions = 40DT, etc.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Survey Criteria Section */}
                    <div className="border-t pt-8">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center">
                                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mr-4">
                                    <i className="fas fa-filter text-purple-500 text-xl"></i>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Critères de sondage</h2>
                                    <p className="text-gray-600 text-sm">
                                        {criteria.length} critère(s) configuré(s)
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Add New Criteria Form */}
                        <div className="mb-8 p-6 bg-gray-50 rounded-xl border">
                            <h3 className="font-semibold text-gray-900 mb-4">
                                <i className="fas fa-plus-circle text-purple-500 mr-2"></i>
                                Ajouter un nouveau critère
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <TaleInput
                                    label="Nom du critère"
                                    value={newCriteria.label}
                                    onChange={(e) => setNewCriteria({
                                        ...newCriteria,
                                        label: e.target.value
                                    })}
                                    placeholder="Ex: Urgence"
                                />
                                <TaleInput
                                    label="Prix supplémentaire (DT)"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={newCriteria.price}
                                    onChange={(e) => setNewCriteria({
                                        ...newCriteria,
                                        price: e.target.value
                                    })}
                                    placeholder="Ex: 15.00"
                                />
                                <div className="flex items-end">
                                    <TaleButton
                                        onClick={() => {
                                            // Implement add criteria if API exists
                                            alert("Fonctionnalité à implémenter");
                                        }}
                                        className="w-full bg-purple-600 hover:bg-purple-700 border-purple-600"
                                    >
                                        <i className="fas fa-plus mr-2"></i>
                                        Ajouter
                                    </TaleButton>
                                </div>
                            </div>
                        </div>

                        {/* Existing Criteria Table */}
                        {criteria.length > 0 ? (
                            <div className="overflow-x-auto border rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Critère
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Slug
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Prix supplémentaire
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {criteria.map((criterion) => (
                                        <tr key={criterion.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                {editingCriteria[criterion.id] ? (
                                                    <div className="w-40">
                                                        <TaleInput
                                                            value={editingCriteria[criterion.id].label}
                                                            onChange={(e) => setEditingCriteria({
                                                                ...editingCriteria,
                                                                [criterion.id]: {
                                                                    ...editingCriteria[criterion.id],
                                                                    label: e.target.value
                                                                }
                                                            })}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="font-medium text-gray-900">{criterion.label}</div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                                                    {criterion.slug}
                                                </code>
                                            </td>
                                            <td className="px-6 py-4">
                                                {editingCriteria[criterion.id] ? (
                                                    <div className="w-32">
                                                        <TaleInput
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={editingCriteria[criterion.id].price}
                                                            onChange={(e) => setEditingCriteria({
                                                                ...editingCriteria,
                                                                [criterion.id]: {
                                                                    ...editingCriteria[criterion.id],
                                                                    price: e.target.value
                                                                }
                                                            })}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="font-medium text-purple-600">
                                                        {formatCurrency(criterion.price)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex gap-2">
                                                    {editingCriteria[criterion.id] ? (
                                                        <>
                                                            <TaleButton
                                                                size="sm"
                                                                onClick={() => handleUpdateCriteria(criterion.id, {
                                                                    label: editingCriteria[criterion.id].label,
                                                                    price: parseFloat(editingCriteria[criterion.id].price)
                                                                })}
                                                                loading={updateCriteriaMutation.isPending}
                                                            >
                                                                <i className="fas fa-check mr-1"></i>
                                                                Valider
                                                            </TaleButton>
                                                            <TaleButton
                                                                size="sm"
                                                                variant="outlined"
                                                                onClick={() => {
                                                                    const newEditing = { ...editingCriteria };
                                                                    delete newEditing[criterion.id];
                                                                    setEditingCriteria(newEditing);
                                                                }}
                                                            >
                                                                Annuler
                                                            </TaleButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <TaleButton
                                                                size="sm"
                                                                variant="outlined"
                                                                onClick={() => setEditingCriteria({
                                                                    ...editingCriteria,
                                                                    [criterion.id]: {
                                                                        label: criterion.label,
                                                                        price: criterion.price
                                                                    }
                                                                })}
                                                            >
                                                                <i className="fas fa-edit mr-1"></i>
                                                                Modifier
                                                            </TaleButton>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <i className="fas fa-filter text-4xl text-gray-300 mb-3"></i>
                                <p className="text-gray-500">Aucun critère de facturation configuré</p>
                                <p className="text-sm text-gray-400 mt-1">Ajoutez votre premier critère ci-dessus</p>
                            </div>
                        )}

                        <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="flex items-start">
                                <i className="fas fa-lightbulb text-purple-500 mt-0.5 mr-3"></i>
                                <div className="text-sm text-purple-700">
                                    Les critères ajoutent des suppléments au prix de base.
                                    Exemple: "Urgence" (+15DT), "Multi-langues" (+25DT), etc.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Billing Preview Section */}
                    <div className="border-t pt-8">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center mr-4">
                                <i className="fas fa-calculator text-orange-500 text-xl"></i>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Simulation de facturation</h2>
                                <p className="text-gray-600 text-sm">
                                    Visualisez comment les tarifs sont calculés
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h3 className="font-semibold text-gray-900">Paramètres de simulation</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-gray-50 p-4 rounded-lg border">
                                        <div className="text-sm text-gray-600 mb-1">Réponses</div>
                                        <div className="text-lg font-semibold">100</div>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg border">
                                        <div className="text-sm text-gray-600 mb-1">Questions</div>
                                        <div className="text-lg font-semibold">15</div>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg border">
                                        <div className="text-sm text-gray-600 mb-1">Critères</div>
                                        <div className="text-lg font-semibold">2</div>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg border">
                                        <div className="text-sm text-gray-600 mb-1">Tranche</div>
                                        <div className="text-lg font-semibold">15 Q</div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                                <h3 className="font-semibold text-gray-900 mb-4">Résultat du calcul</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">100 réponses × {formatCurrency(responsePricing?.pricePerResponse || 0)}</span>
                                        <span>{formatCurrency(100 * (responsePricing?.pricePerResponse || 0))}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Tranche (15 questions)</span>
                                        <span className="text-green-600">+ {formatCurrency(25)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Critères appliqués (2)</span>
                                        <span className="text-purple-600">+ {formatCurrency(30)}</span>
                                    </div>
                                    <div className="pt-3 border-t">
                                        <div className="flex justify-between items-center">
                                            <span className="font-semibold text-gray-900">Total estimé</span>
                                            <span className="text-2xl font-bold text-blue-600">
                                                {formatCurrency(100 * (responsePricing?.pricePerResponse || 0) + 25 + 30)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* How it Works Section */}
                    <div className="border-t pt-8">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mr-4">
                                <i className="fas fa-question-circle text-gray-500 text-xl"></i>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Comment fonctionne la facturation ?</h2>
                                <p className="text-gray-600 text-sm">
                                    Comprenez la logique de tarification
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                            <div className="p-6 bg-blue-50 rounded-xl border border-blue-200">
                                <div className="flex items-center mb-4">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                        <i className="fas fa-receipt text-blue-500"></i>
                                    </div>
                                    <div className="font-semibold text-blue-700">Prix de base</div>
                                </div>
                                <p className="text-sm text-blue-600">
                                    Chaque réponse reçue coûte un montant fixe. Ce coût s'applique à toutes les réponses.
                                </p>
                            </div>

                            <div className="p-6 bg-green-50 rounded-xl border border-green-200">
                                <div className="flex items-center mb-4">
                                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                                        <i className="fas fa-layer-group text-green-500"></i>
                                    </div>
                                    <div className="font-semibold text-green-700">Tranches de questions</div>
                                </div>
                                <p className="text-sm text-green-600">
                                    Le nombre de questions détermine une tranche de prix. Plus de questions = prix plus élevé.
                                </p>
                            </div>

                            <div className="p-6 bg-purple-50 rounded-xl border border-purple-200">
                                <div className="flex items-center mb-4">
                                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                                        <i className="fas fa-filter text-purple-500"></i>
                                    </div>
                                    <div className="font-semibold text-purple-700">Critères supplémentaires</div>
                                </div>
                                <p className="text-sm text-purple-600">
                                    Des critères spécifiques (urgence, complexité) ajoutent des suppléments au coût total.
                                </p>
                            </div>
                        </div>

                        <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200">
                            <div className="flex items-start">
                                <i className="fas fa-calculator text-yellow-500 text-xl mr-4"></i>
                                <div>
                                    <h4 className="font-bold text-yellow-800 mb-2">Formule de calcul</h4>
                                    <div className="text-sm text-yellow-700 font-mono bg-yellow-100 p-3 rounded-lg">
                                        Coût total = (Nombre de réponses × Prix par réponse) +<br/>
                                        Tranche(nombre de questions) + Σ(Critères appliqués)
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </TaleDashbaordMainCard>
    );
};

export default BillingConfig;
